export type ActivityType =
  | 'generic'
  | '3min-test'
  | '6min-test'
  | 'tug'
  | '25ft-walk'
  | '10m-walk'
  | 'static-10m-walk'
  | 'dual-task-10m-walk'
  | '4m-walk'
  | 'static-4m-walk'
  | 'dual-task-4m-walk'
  | 'stair-climb'
  | 'posturography'
  | 'auto-logging'
  | 'mini-squat'
  | 'semi-tandem-walk'
  | 'lateral-dodging'
  | 'tandem-pivot'
  | 'timed'
  | 'tuning-fork'
  | 'tandem-walk'
  | 'in-place-turns'
  | 'pull-test'
  | 'heel-raises'
  | 'toe-tapping'
  | 'choice-haptic-reaction-time'
  | 'simple-haptic-reaction-time';

export type VibrationMode =
  | 'none'
  | 'hs-ho-feedback'
  | 'metronome'
  | 'pink-metronome'
  | 'load-vibrate'
  | 'unload-vibrate'
  | 'adaptive-metronome'
  | 'circumduction-vibrate'
  | 'swing-plus-vibrate'
  | 'swing-vibrate'
  | 'crescendo-vibrate'
  | 'heel-off-vibrate'
  | 'modulation-vibrate'
  | 'length-vibrate';

export interface Activity {
  id: string;
  activityStreamId?: string; // The stream ID for the "activity" stream (used for downloads)
  analysisState?: ActivityAnalysisState;
  streamIds?: string[];
  patientId: string;
  patientName?: string;
  patientUsername?: string;
  type: ActivityType;
  title: string;
  vibrationMode: VibrationMode;
  timestamp: string;
  duration?: number;
  steps?: number;
  distance?: number;
}

export type ActivityAnalysisState = 'failed' | 'unanalyzed';

function normalizeStreamId(streamId: string): string {
  return streamId.toLowerCase().replace(/_/g, '-');
}

export function getActivityAnalysisState(
  activity: Pick<Activity, 'analysisState' | 'streamIds'>
): ActivityAnalysisState | undefined {
  if (activity.analysisState) {
    return activity.analysisState;
  }

  const normalizedStreamIds = activity.streamIds?.map(normalizeStreamId) ?? [];

  if (normalizedStreamIds.includes('failed')) {
    return 'failed';
  }

  if (
    normalizedStreamIds.includes('unanalyzed') ||
    normalizedStreamIds.includes('unanalysed')
  ) {
    return 'unanalyzed';
  }

  return undefined;
}

function normalizeActivityLabel(value: string): string {
  return value.trim().toLowerCase().replace(/_/g, '-');
}

export function getActivityDisplayTitle(activity: Pick<Activity, 'title' | 'type'>): string {
  const fallbackLabel = ACTIVITY_TYPE_LABELS[activity.type];
  const rawTitle = activity.title?.trim();

  if (!rawTitle) {
    return fallbackLabel;
  }

  const normalizedTitle = normalizeActivityLabel(rawTitle);

  if (
    normalizedTitle === normalizeActivityLabel(activity.type) ||
    normalizedTitle === normalizeActivityLabel(fallbackLabel)
  ) {
    return fallbackLabel;
  }

  return rawTitle;
}

// Note: API has typo "ActivityResut" instead of "ActivityResult"
export interface ActivityResut {
  activity_id: string;
  results?: PryvEvent | null;
  meta?: PryvEvent | null;
  macro?: PryvEvent[] | null;
}

export interface ActivityRawData {
  activity_id: string;
  raw_data: PryvEvent | null;
}

// Alias for backwards compatibility
export type ActivityResult = ActivityResut;

// PryvEvent type from OpenAPI spec
export interface PryvEvent {
  type: string;
  streamIds: unknown[];
  time: number;
  created: number;
  createdBy: string;
  modified: number;
  modifiedBy: string;
  id: string;
  streamId?: string | null;
  content: unknown;
  attachments: Record<string, unknown>[];
  attachements: Record<string, unknown>[]; // Note: typo in API
  duration: number;
  trashed: boolean;
}

export interface ActivityFilters {
  types?: ActivityType[];
  vibrationModes?: VibrationMode[];
  startDate?: string;
  endDate?: string;
}

export interface ActivityListResponse {
  activities: Activity[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PatientActivityDownload {
  token: string;
  userId: string;
  activities: string[];
}

export interface DownloadActivityRequest {
  data: PatientActivityDownload[];
  includeMacro?: boolean;
  includeMeta?: boolean;
}

// Patient-specific download types (for /patient/activities)
export interface PatientDownloadActivitiesBody {
  activities: string[]; // Array of activity IDs to download
  includeMacro?: boolean;
  includeMeta?: boolean;
}

export const ACTIVITY_TYPES: ActivityType[] = [
  'generic',
  '3min-test',
  '6min-test',
  'tug',
  '25ft-walk',
  '10m-walk',
  'static-10m-walk',
  'dual-task-10m-walk',
  '4m-walk',
  'static-4m-walk',
  'dual-task-4m-walk',
  'stair-climb',
  'posturography',
  'auto-logging',
  'mini-squat',
  'semi-tandem-walk',
  'lateral-dodging',
  'tandem-pivot',
  'timed',
  'tuning-fork',
  'tandem-walk',
  'in-place-turns',
  'pull-test',
  'heel-raises',
  'toe-tapping',
  'choice-haptic-reaction-time',
  'simple-haptic-reaction-time',
];

// Activity groupings for organized filtering
export const GAIT_ACTIVITY_TYPES: ActivityType[] = [
  'generic',
  '3min-test',
  '6min-test',
  'tug',
  '10m-walk',
  'static-10m-walk',
  'dual-task-10m-walk',
  '4m-walk',
  'static-4m-walk',
  'dual-task-4m-walk',
  '25ft-walk',
  'auto-logging',
  'timed',
];

export const NON_GAIT_ACTIVITY_TYPES: ActivityType[] = [
  'posturography',
  'heel-raises',
  'toe-tapping',
  'choice-haptic-reaction-time',
  'simple-haptic-reaction-time',
];

export const EXPERIMENTAL_ACTIVITY_TYPES: ActivityType[] = [
  'stair-climb',
  'tuning-fork',
  'tandem-walk',
  'in-place-turns',
  'pull-test',
  // Note: Missing from current ActivityType: 'stairs', 'figure-eight', 'circumnavigation-walk'
];

export interface ActivityGroup {
  id: string;
  label: string;
  types: ActivityType[];
}

export const ACTIVITY_GROUPS: ActivityGroup[] = [
  {
    id: 'gait',
    label: 'Gait Activities',
    types: GAIT_ACTIVITY_TYPES,
  },
  {
    id: 'non-gait',
    label: 'Non-Gait Activities',
    types: NON_GAIT_ACTIVITY_TYPES,
  },
  {
    id: 'experimental',
    label: 'Experimental Activities',
    types: EXPERIMENTAL_ACTIVITY_TYPES,
  },
];

export const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  'generic': 'Generic',
  '3min-test': '3min Test',
  '6min-test': '6min Test',
  'tug': 'TUG',
  '25ft-walk': '25ft Walk',
  '10m-walk': '10m Walk',
  'static-10m-walk': 'Static 10m Walk',
  'dual-task-10m-walk': 'Dual Task 10m Walk',
  '4m-walk': '4m Walk',
  'static-4m-walk': 'Static 4m Walk',
  'dual-task-4m-walk': 'Dual Task 4m Walk',
  'stair-climb': 'Stair Climb',
  'posturography': 'Posturography',
  'auto-logging': 'Auto Logging',
  'mini-squat': 'Mini Squat',
  'semi-tandem-walk': 'Semi Tandem Walk',
  'lateral-dodging': 'Lateral Dodging',
  'tandem-pivot': 'Tandem Pivot',
  'timed': 'Timed',
  'tuning-fork': 'Tuning Fork',
  'tandem-walk': 'Tandem Walk',
  'in-place-turns': 'In-place Turns',
  'pull-test': 'Pull Test',
  'heel-raises': 'Heel Raises',
  'toe-tapping': 'Toe Tapping',
  'choice-haptic-reaction-time': 'Choice Haptic Reaction Time',
  'simple-haptic-reaction-time': 'Simple Haptic Reaction Time',
};

export const VIBRATION_MODES: VibrationMode[] = [
  'none',
  'hs-ho-feedback',
  'metronome',
  'pink-metronome',
  'load-vibrate',
  'unload-vibrate',
  'adaptive-metronome',
  'circumduction-vibrate',
  'swing-plus-vibrate',
  'swing-vibrate',
  'crescendo-vibrate',
  'heel-off-vibrate',
  'modulation-vibrate',
  'length-vibrate',
];

export const VIBRATION_MODE_LABELS: Record<VibrationMode, string> = {
  'none': 'None',
  'hs-ho-feedback': 'HS-HO Feedback',
  'metronome': 'Metronome',
  'pink-metronome': 'Pink Metronome',
  'load-vibrate': 'Load Vibrate',
  'unload-vibrate': 'Unload Vibrate',
  'adaptive-metronome': 'Adaptive Metronome',
  'circumduction-vibrate': 'Circumduction Vibrate',
  'swing-plus-vibrate': 'Swing Plus Vibrate',
  'swing-vibrate': 'Swing Vibrate',
  'crescendo-vibrate': 'Crescendo Vibrate',
  'heel-off-vibrate': 'Heel Off Vibrate',
  'modulation-vibrate': 'Modulation Vibrate',
  'length-vibrate': 'Length Vibrate',
};

export function isActivitySelectable(activity: Activity): boolean {
  const analysisState = getActivityAnalysisState(activity);
  return analysisState !== 'failed' && analysisState !== 'unanalyzed';
}

export function getActivitySelectionDisabledReason(activity: Activity): string | undefined {
  const analysisState = getActivityAnalysisState(activity);

  if (analysisState === 'failed') {
    return 'This activity could not be analyzed, possibly because there was not enough data or the data was corrupted. Please contact Magnes Support if you think this is incorrect.';
  }

  if (analysisState === 'unanalyzed') {
    return 'This activity has not yet been analysed. Please wait a couple of minutes and it should be automatically analysed.';
  }

  return undefined;
}

export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
}
